import type { NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import Grid from '../../components/grid/Grid'
import CustomImage from '../../components/customImage/CustomImage'
import styles from '../../styles/pages/News.module.scss'
import Contact from '../../components/contact/Contact'
import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'
import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'
import { useIsMobile } from '../../hooks/useIsDesktop'
import { useGlobalContentContext } from '../../context/GlobalContentContext'

import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'

import NewsPreviewItem, { NewsProps } from '../../components/news/News'

const NUM_OF_ARTICLES = 6
type Props = {
  articles: any
} & GenericPageLayoutProps

type LinkBlockProps = {
  href: string
  children: any
  hideArrow?: boolean
  showBorder?: string
}

const LinkBlock = (props: LinkBlockProps) => {
  let borderClass = styles.linkBlock
  const isMobile = useIsMobile()

  switch (props.showBorder) {
    case 'right':
      borderClass = styles.linkBlockBorderRight
      break
    case 'right-bottom':
      borderClass = styles.linkBlockBorderRightBottom
      break
    case 'top':
      borderClass = styles.linkBlockBorder
      break
    case 'bottom':
      borderClass = styles.linkBlockBorderBottom
      break
    default:
      break
  }
  return (
    <div className={borderClass}>
      {!props.hideArrow && !isMobile && (
        <Link href={props.href}>
          <a className={styles.linkArrow}>
            <Image src="/arrow-black.svg" layout="fill" alt="" />
          </a>
        </Link>
      )}
      <Link href={props.href}>
        <a className={styles.linkTitle}>{props.children}</a>
      </Link>
      {!props.hideArrow && isMobile && (
        <Link href={props.href}>
          <a className={styles.linkArrow}>
            <Image src="/arrow-black.svg" layout="fill" alt="" />
          </a>
        </Link>
      )}
    </div>
  )
}

const News = (props: Props) => {
  const { setData } = useGlobalContentContext()
  const isMobile = useIsMobile()

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  const [contactForm, showContactForm] = useState(false)

  const articles = props.articles.slice(0, NUM_OF_ARTICLES)

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroContentContainer}>
            <h1 className={styles.heroTitle}>{props.originalResponse.title}</h1>
          </div>
          <div className={styles.heroImage}>
            <CustomImage
              src={props.originalResponse}
              layout="fill"
              objectFit="cover"
            />
          </div>
        </section>

        <section className={styles.newsItems}>
          <h2 className={styles.newsItemsTitle}>Latest News</h2>

          <Grid columns={3}>
            {articles.map((item: any, index: number) => (
              <div className={styles.newsItem} key={index}>
                <NewsPreviewItem
                  item={item}
                  showPubDateTop={true}
                  showDescription={true}
                  showReadMore={true}
                />
              </div>
            ))}
          </Grid>
        </section>

        <section className={styles.linksSection}>
          <Grid columns={3}>
            <LinkBlock showBorder="top" href="/downloads">
              <span>
                {props.originalResponse.newsstatisticsblocks.downloads_title}
              </span>
            </LinkBlock>

            <LinkBlock showBorder="top" href="/company">
              <span>
                {props.originalResponse.newsstatisticsblocks.company_title}
              </span>
            </LinkBlock>

            <div
              className={styles.linkBlockBorder}
              onClick={() => showContactForm(!contactForm)}
            >
              {!isMobile && (
                <span
                  className={`${styles.linkArrow} ${
                    contactForm ? styles.linkArrowRotate : ''
                  }`}
                >
                  <Image src="/arrow-black.svg" layout="fill" alt="" />
                </span>
              )}
              <span className={styles.linkTitle}>
                <span>
                  {props.originalResponse.newsstatisticsblocks.contact_title}
                </span>
              </span>
              {isMobile && (
                <span
                  className={`${styles.linkArrow} ${
                    contactForm ? styles.linkArrowRotate : ''
                  }`}
                >
                  <Image src="/arrow-black.svg" layout="fill" alt="" />
                </span>
              )}
            </div>
          </Grid>

          {contactForm && (
            <>
              <Contact hideTitle={true} />
            </>
          )}
        </section>
        <section className={styles.linksSectionGreen}>
          <div className={styles.maxWidthContainer}>
            <Grid columns={3} gap="0">
              <LinkBlock
                href="/company#history"
                hideArrow={true}
                showBorder="right-bottom"
              >
                <div className={styles.linkWithDetail}>
                  <p className={styles.linkSmallTitle}>
                    {props.originalResponse.newsstatisticsblocks.about_title}
                  </p>

                  <p>
                    {props.originalResponse.newsstatisticsblocks.about_subtitle}
                  </p>
                  <div className={styles.linkDetail}>
                    {props.originalResponse.newsstatisticsblocks.about_detail}
                  </div>
                </div>
              </LinkBlock>

              {/* <LinkBlock
                href="/company#leadership"
                hideArrow={true}
                showBorder="right-bottom"
              > */}
              <div className={styles.linkBlockBorderRightBottom}>
                <div className={styles.noLinkTitle}>
                  <div className={styles.linkWithDetail}>
                    <p className={styles.linkSmallTitle}>
                      {
                        props.originalResponse.newsstatisticsblocks
                          .leadership_title
                      }
                    </p>
                    <p>
                      {
                        props.originalResponse.newsstatisticsblocks
                          .leadership_subtitle
                      }
                    </p>
                    <div className={styles.linkDetail}>
                      {
                        props.originalResponse.newsstatisticsblocks
                          .leadership_detail
                      }
                    </div>
                  </div>
                </div>
              </div>
              {/* </LinkBlock> */}

              <LinkBlock href="/careers" hideArrow={true} showBorder="bottom">
                <div className={styles.linkWithDetail}>
                  <p className={styles.linkSmallTitle}>
                    {props.originalResponse.newsstatisticsblocks.team_title}
                  </p>
                  <p>
                    {props.originalResponse.newsstatisticsblocks.team_subtitle}
                  </p>
                  <div className={styles.linkDetail}>
                    {props.originalResponse.newsstatisticsblocks.team_detail}
                  </div>
                </div>
              </LinkBlock>

              <LinkBlock href="/company" hideArrow={true} showBorder="right">
                <div className={styles.linkWithDetail}>
                  <p className={styles.linkSmallTitle}>
                    {props.originalResponse.newsstatisticsblocks.location_title}
                  </p>
                  <p>
                    {
                      props.originalResponse.newsstatisticsblocks
                        .location_subtitle
                    }
                  </p>
                  <div className={styles.linkDetail}>
                    {
                      props.originalResponse.newsstatisticsblocks
                        .location_detail
                    }
                  </div>
                </div>
              </LinkBlock>

              <LinkBlock
                href="/company#partners"
                hideArrow={true}
                showBorder="right"
              >
                <div className={styles.linkWithDetail}>
                  <p className={styles.linkSmallTitle}>
                    {
                      props.originalResponse.newsstatisticsblocks
                        .collaborators_title
                    }
                  </p>

                  <p>
                    {
                      props.originalResponse.newsstatisticsblocks
                        .collaborators_subtitle
                    }
                  </p>
                  <div className={styles.linkDetail}>
                    {
                      props.originalResponse.newsstatisticsblocks
                        .collaborators_detail
                    }
                  </div>
                </div>
              </LinkBlock>

              <LinkBlock href="/manufacturing#map" hideArrow={true}>
                <div className={styles.linkWithDetail}>
                  <p className={styles.linkSmallTitle}>
                    {
                      props.originalResponse.newsstatisticsblocks
                        .factories_title
                    }
                  </p>
                  <p>
                    {
                      props.originalResponse.newsstatisticsblocks
                        .factories_subtitle
                    }
                  </p>

                  <div className={styles.linkDetail}>
                    {
                      props.originalResponse.newsstatisticsblocks
                        .factories_detail
                    }
                  </div>
                </div>
              </LinkBlock>
            </Grid>
          </div>
        </section>

        <section className={styles.allNews}>
          {props.articles.length > NUM_OF_ARTICLES && (
            <>
              <h2 className={styles.allNewsTitle}>All News</h2>
              <Grid columns={3}>
                {props.articles
                  .slice(NUM_OF_ARTICLES)
                  .map((item: any, index: number) => (
                    <div className={styles.newsItem} key={index}>
                      <NewsPreviewItem
                        item={item}
                        showPubDate={true}
                        showDescription={true}
                      />
                    </div>
                  ))}
              </Grid>
            </>
          )}
        </section>
      </div>
    </div>
  )
}

export async function getStaticProps() {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/news/`
  )

  const articlesRequest = await fetch(`${getAPI()}/news-articles/`)
  const allArticles = await articlesRequest.json()
  const articles = allArticles.filter(
    (article: any) => article.pub_status == 30
  )

  return {
    props: {
      originalResponse: page.originalResponse,
      articles: articles,
      globalData: page.globalData,
      title: page.title,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default News
